import React, { useState, useEffect } from 'react';

type InterestType = 'reducing' | 'fixed' | 'simple' | 'compound';
type CompoundingFrequency = 'monthly' | 'quarterly' | 'semi-annually' | 'annually';

interface EMIDetails {
  payment: number; // Monthly/periodic payment
  totalInterest: number;
  totalPayment: number;
  effectiveRate: number; // Annual effective interest rate
  schedule: Array<{
    period: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
  }>;
}

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState<string>('');
  const [interestRate, setInterestRate] = useState<string>('');
  const [loanTenure, setLoanTenure] = useState<string>('');
  const [tenureType, setTenureType] = useState<'years' | 'months'>('years');
  const [interestType, setInterestType] = useState<InterestType>('reducing');
  const [compoundingFrequency, setCompoundingFrequency] = useState<CompoundingFrequency>('annually');
  const [emiDetails, setEmiDetails] = useState<EMIDetails | null>(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);

  const calculatePayments = () => {
    const P = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate) / 100;
    const N = tenureType === 'years' ? parseFloat(loanTenure) * 12 : parseFloat(loanTenure); // Total number of months

    if (isNaN(P) || isNaN(annualRate) || isNaN(N)) return null;

    let payment = 0;
    let totalInterest = 0;
    let totalPayment = 0;
    let effectiveRate = annualRate;
    let schedule: EMIDetails['schedule'] = [];

    switch (interestType) {
      case 'reducing': {
        // Standard reducing balance EMI
        const monthlyRate = annualRate / 12;
        payment = P * monthlyRate * Math.pow(1 + monthlyRate, N) / (Math.pow(1 + monthlyRate, N) - 1);
        
        let balance = P;
        for (let month = 1; month <= N; month++) {
          const interest = balance * monthlyRate;
          const principal = payment - interest;
          balance = Math.max(0, balance - principal);

          schedule.push({
            period: month,
            payment,
            principal,
            interest,
            balance
          });
        }
        break;
      }

      case 'fixed': {
        // Flat rate where interest is calculated on full principal
        const monthlyRate = annualRate / 12;
        const monthlyInterest = P * monthlyRate;
        const monthlyPrincipal = P / N;
        payment = monthlyPrincipal + monthlyInterest;

        let balance = P;
        for (let month = 1; month <= N; month++) {
          const principal = monthlyPrincipal;
          const interest = monthlyInterest;
          balance = Math.max(0, balance - principal);

          schedule.push({
            period: month,
            payment,
            principal,
            interest,
            balance
          });
        }
        break;
      }

      case 'simple': {
        // Simple interest where total interest is calculated upfront
        const totalInterest = P * annualRate * (N / 12);
        const monthlyPayment = (P + totalInterest) / N;
        
        let balance = P;
        const monthlyPrincipal = P / N;
        const monthlyInterest = totalInterest / N;

        for (let month = 1; month <= N; month++) {
          balance = Math.max(0, balance - monthlyPrincipal);
          
          schedule.push({
            period: month,
            payment: monthlyPayment,
            principal: monthlyPrincipal,
            interest: monthlyInterest,
            balance
          });
        }
        payment = monthlyPayment;
        break;
      }

      case 'compound': {
        // Compound interest with different frequencies
        let periodsPerYear: number;
        switch (compoundingFrequency) {
          case 'monthly': periodsPerYear = 12; break;
          case 'quarterly': periodsPerYear = 4; break;
          case 'semi-annually': periodsPerYear = 2; break;
          case 'annually': periodsPerYear = 1; break;
          default: periodsPerYear = 12;
        }

        // Calculate effective annual rate
        effectiveRate = Math.pow(1 + annualRate / periodsPerYear, periodsPerYear) - 1;
        
        // Convert to monthly rate for payment calculation
        const monthlyRate = Math.pow(1 + effectiveRate, 1/12) - 1;
        payment = P * monthlyRate * Math.pow(1 + monthlyRate, N) / (Math.pow(1 + monthlyRate, N) - 1);

        let balance = P;
        for (let month = 1; month <= N; month++) {
          const interest = balance * monthlyRate;
          const principal = payment - interest;
          balance = Math.max(0, balance - principal);

          schedule.push({
            period: month,
            payment,
            principal,
            interest,
            balance
          });
        }
        break;
      }
    }

    totalPayment = payment * N;
    totalInterest = totalPayment - P;

    return {
      payment,
      totalInterest,
      totalPayment,
      effectiveRate,
      schedule
    };
  };

  const handleCalculate = () => {
    const details = calculatePayments();
    if (details) {
      setEmiDetails(details);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4 p-6 bg-white rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4">Loan Details</h3>
          
          <div className="mb-4">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={advancedMode}
                onChange={(e) => setAdvancedMode(e.target.checked)}
                className="form-checkbox h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-sm text-gray-700">Advanced Mode</span>
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan Amount (₹)
            </label>
            <input
              type="number"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter loan amount"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interest Rate (% per annum)
            </label>
            <input
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter interest rate"
              step="0.1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loan Tenure
              </label>
              <input
                type="number"
                value={loanTenure}
                onChange={(e) => setLoanTenure(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tenure"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenure Type
              </label>
              <select
                value={tenureType}
                onChange={(e) => setTenureType(e.target.value as 'years' | 'months')}
                className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="years">Years</option>
                <option value="months">Months</option>
              </select>
            </div>
          </div>

          {advancedMode && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interest Calculation Method
                </label>
                <select
                  value={interestType}
                  onChange={(e) => setInterestType(e.target.value as InterestType)}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="reducing">Reducing Balance (EMI)</option>
                  <option value="fixed">Fixed Interest (Flat Rate)</option>
                  <option value="simple">Simple Interest</option>
                  <option value="compound">Compound Interest</option>
                </select>
              </div>

              {interestType === 'compound' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Compounding Frequency
                  </label>
                  <select
                    value={compoundingFrequency}
                    onChange={(e) => setCompoundingFrequency(e.target.value as CompoundingFrequency)}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="semi-annually">Semi-annually</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleCalculate}
            className="w-full mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Calculate EMI
          </button>
        </div>

        {emiDetails && (
          <div className="space-y-4 p-6 bg-white rounded-lg shadow">
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Disclaimer:</span> These are indicative figures, please refer to the original loan document for actual numbers.
              </p>
            </div>
            <h3 className="text-lg font-medium mb-4">EMI Details</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">Monthly Payment</span>
                <span className="font-medium">{formatCurrency(emiDetails.payment)}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">Total Interest</span>
                <span className="font-medium">{formatCurrency(emiDetails.totalInterest)}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">Total Payment</span>
                <span className="font-medium">{formatCurrency(emiDetails.totalPayment)}</span>
              </div>
              
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Principal Amount</span>
                <span className="font-medium">{formatCurrency(parseFloat(loanAmount))}</span>
              </div>
            </div>

            <button
              onClick={() => setShowSchedule(!showSchedule)}
              className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              {showSchedule ? 'Hide' : 'Show'} Amortization Schedule
            </button>
          </div>
        )}
      </div>

      {emiDetails && showSchedule && (
        <div className="mt-6 p-6 bg-white rounded-lg shadow overflow-x-auto">
          <h3 className="text-lg font-medium mb-4">Amortization Schedule</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EMI</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interest</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {emiDetails.schedule.map((row) => (
                <tr key={row.period}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.period}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.payment)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.principal)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.interest)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(row.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EMICalculator;
